import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Input,
  ViewChild,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { ButtonComponent } from '@shared/components/button/button.component';
import {
  AlternativeAnswerSingle,
  SingleSelect,
} from '@shared/models/question-types/single-select';
@Component({
  selector: 'app-slider',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    CommonModule,
    ButtonComponent,
  ],
  templateUrl: './slider.component.html',
  styleUrl: './slider.component.scss',
})
export class SliderComponent implements AfterViewInit {
  private readonly THUMB_SIZE_PX = 40;
  // Space between the block's own top edge and the track's vertical center
  // when inMatrix, excluding the dynamic matrixTrackTopPaddingPx below.
  private readonly MATRIX_ROOT_TOP_OFFSET_PX = 16 + 4; // root pt-4 + half of the h-2 track
  private readonly MATRIX_MIN_TRACK_TOP_PADDING_PX = 16; // headroom for the value tooltip
  @Input({ required: true }) question!: SingleSelect;
  @Input({ required: true }) control!: any;
  @Input() rowTitle: string = '';
  @Input() inMatrix: boolean = false;

  @ViewChild('rangeInput', { static: false })
  rangeInput!: ElementRef<HTMLInputElement>;
  @ViewChild('startLabel', { static: false })
  startLabel!: ElementRef<HTMLElement>;
  @ViewChild('belowContent', { static: false })
  belowContent!: ElementRef<HTMLElement>;

  selectedAlternativeIndex = 0;
  tooltipPosition = 0;
  sliderValue: string | undefined = '';
  hasImage: AlternativeAnswerSingle | undefined;
  matrixTrackTopPaddingPx = this.MATRIX_MIN_TRACK_TOP_PADDING_PX;

  constructor(private cdr: ChangeDetectorRef) {}

  ngAfterViewInit() {
    setTimeout(() => {
      this.hasImage = this.question.alternatives.find((alt) => alt.image);
      this.updateInitialSliderValue();
      this.updateInitialTooltipPosition();
      this.recalculateMatrixTrackCentering();
    }, 0);
  }

  // Below-track content (labels + optional clear button) has no fixed height
  // (button is optional, labels can wrap), so the only way to keep the track
  // exactly centered in the matrix cell is to measure it and mirror its
  // height as top padding, rather than guessing static Tailwind values.
  recalculateMatrixTrackCentering() {
    if (!this.inMatrix) {
      return;
    }

    const belowHeight = this.belowContent?.nativeElement.offsetHeight ?? 0;
    this.matrixTrackTopPaddingPx = Math.max(
      this.MATRIX_MIN_TRACK_TOP_PADDING_PX,
      belowHeight - this.MATRIX_ROOT_TOP_OFFSET_PX,
    );
  }

  updateInitialSliderValue() {
    if (this.control.value !== null && this.control.value !== undefined) {
      this.sliderValue = this.question.alternatives.find(
        (i) => i.orderNumber === this.control.value,
      )?.title;
    } else {
      if (this.rangeInput?.nativeElement) {
        this.rangeInput.nativeElement.value = String(0);
      }
    }
  }

  onSliderChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.control.setValue(target.value);
    this.cdr.detectChanges();
  }

  updateTooltip(event: Event) {
    this.updateTooltipPosition(event);
    this.updateSliderValue();
  }

  updateTooltipPosition(event: Event) {
    const rangeInput = event.target as HTMLInputElement;
    this.tooltipPosition = this.calculateTooltipPosition(rangeInput);
  }

  updateSliderValue() {
    this.sliderValue = this.question.alternatives.find(
      (i) => i.orderNumber === this.control.value,
    )?.title;
  }

  updateInitialTooltipPosition() {
    this.tooltipPosition = this.calculateTooltipPosition(
      this.rangeInput.nativeElement,
    );
  }

  @HostListener('window:resize')
  onWindowResize() {
    if (!this.rangeInput?.nativeElement) {
      return;
    }

    requestAnimationFrame(() => {
      this.tooltipPosition = this.calculateTooltipPosition(
        this.rangeInput.nativeElement,
      );
      this.recalculateMatrixTrackCentering();
      this.cdr.detectChanges();
    });
  }

  private calculateTooltipPosition(rangeInput: HTMLInputElement): number {
    if (!rangeInput) {
      return 0;
    }

    const sliderWidth = rangeInput.offsetWidth;
    if (sliderWidth === 0) {
      return 0;
    }

    const min = Number(rangeInput.min);
    const max = Number(rangeInput.max);
    if (max - min === 0) {
      return sliderWidth / 2;
    }

    const ratio = (Number(rangeInput.value) - min) / (max - min);
    const usableWidth = Math.max(sliderWidth - this.THUMB_SIZE_PX, 0);
    const position = this.THUMB_SIZE_PX / 2 + ratio * usableWidth;

    return document.documentElement.dir === 'rtl'
      ? sliderWidth - position
      : position;
  }

  clearValue() {
    this.control.reset();
    this.sliderValue = '';

    if (this.rangeInput?.nativeElement) {
      this.rangeInput.nativeElement.value = String(0);
    }
  }
}
